<template>
    <div v-if="errors.length > 0" class="alert alert-danger py-2 mb-3">
        <ul class="mb-0 ps-3">
            <li v-for="err in errors" :key="err">{{ err }}</li>
        </ul>
    </div>

    <div class="row">
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Assigned To <span class="text-danger">*</span></span>
                    <tech-ac 
                        v-model="form.AssignedTo" 
                        placeholder="Search for a technician..."
                        @change="touched.assignedTo = true"
                    ></tech-ac>
                    <div v-if="touched.assignedTo && !form.AssignedTo" class="invalid-feedback d-block">Assigned To is required.</div>
                </label>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Client <span class="text-danger">*</span></span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm" 
                        placeholder="Search for a client..."
                        @change="touched.client = true"
                    ></client-ac>
                    <div v-if="touched.client && !form.CustomerNumber" class="invalid-feedback d-block">Client is required.</div>
                </label>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Request/Job <span class="text-danger">*</span></span>
                    <job-ac 
                        v-model="form.RequestId" 
                        :client="form.CustomerNumber"
                        placeholder="Search for a request..."
                        @change="touched.requestId = true"
                    ></job-ac>
                    <div v-if="touched.requestId && !form.RequestId" class="invalid-feedback d-block">Request/Job is required.</div>
                </label>
            </div>
        </div>
    </div>

    <div class="row">
        <div :class="form.Id ? 'col-md-4' : 'col-md-6'">
            <label class="d-block mb-3">
                <span>Estimated Time <span class="text-danger">*</span></span>
                <input 
                    :class="['form-control form-control-sm', touched.estimatedTime && !form.EstimatedTime ? 'is-invalid' : '']" 
                    type="text" 
                    v-model="form.EstimatedTime" 
                    placeholder="Hour Estimate" 
                    @blur="touched.estimatedTime = true"
                />
                <div v-if="touched.estimatedTime && !form.EstimatedTime" class="invalid-feedback">Estimated Time is required.</div>
            </label>
        </div>
        <div v-if="form.Id" class="col-md-4">
            <label class="d-block mb-3">
                <span>Status</span>
                <select 
                    class="form-select form-select-sm" 
                    v-model="form.Status"
                >
                    <option :value="undefined">Select Status...</option>
                    <option 
                        v-for="status in statusOptions" 
                        :key="status.value" 
                        :value="status.value"
                    >
                        {{ status.label }}
                    </option>
                </select>
            </label>
        </div>
        <div :class="form.Id ? 'col-md-4' : 'col-md-6'">
            <label class="d-block mb-3">
                <span>Start By <span class="text-danger">*</span></span>
                <input type="datetime-local" 
                    :class="['form-control form-control-sm', touched.completeByDate && !form.CompleteByDate ? 'is-invalid' : '']" 
                    v-model="form.CompleteByDate" 
                    placeholder="Start By"
                    @blur="touched.completeByDate = true"
                    @change="touched.completeByDate = true"
                >
                <div v-if="touched.completeByDate && !form.CompleteByDate" class="invalid-feedback">Start By is required.</div>
            </label>
        </div>  
    </div>

    <div class="row">
        <div class="col-12">
            <label class="d-block mb-3">
                <span>Work Assignment <span class="text-danger">*</span></span>
                <textarea 
                    :class="['form-control form-control-sm', touched.workAssignment && !form.WorkAssignment?.trim() ? 'is-invalid' : '']" 
                    placeholder="Work Assignment" 
                    v-model="form.WorkAssignment"
                    rows="6"
                    @blur="touched.workAssignment = true"
                ></textarea>
                <div v-if="touched.workAssignment && !form.WorkAssignment?.trim()" class="invalid-feedback">Work Assignment is required.</div>
            </label>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from "vue";
import TechAc from "@/components/Techs/TechAc.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import { storeToRefs } from "pinia";

const model = defineModel<any>();

const taskStatusStore = useTaskStatusStore();
const { list: taskStatusList } = storeToRefs(taskStatusStore);

// Convert store list to options array for select dropdown
const statusOptions = computed(() => 
    Object.values(taskStatusList.value).map(status => ({
        value: status.Id,
        label: status.Status
    }))
);

// Load task statuses
taskStatusStore.load();

const form = computed({
    get: () => {
        // Ensure Type is set to 'assignment'
        if (model.value && model.value.Type !== 'assignment') {
            model.value.Type = 'assignment';
        }
        return model.value || {};
    },
    set: (value) => {
        if (value) {
            value.Type = 'assignment';
        }
        model.value = value;
    }
});

const touched = reactive({
    assignedTo: false,
    client: false,
    requestId: false,
    estimatedTime: false,
    completeByDate: false,
    workAssignment: false,
});

const errors = ref<string[]>([]);

const validate = (): boolean => {
    touched.assignedTo = true;
    touched.client = true;
    touched.requestId = true;
    touched.estimatedTime = true;
    touched.completeByDate = true;
    touched.workAssignment = true;

    const errs: string[] = [];
    if (!form.value.AssignedTo) errs.push('Assigned To is required.');
    if (!form.value.CustomerNumber) errs.push('Client is required.');
    if (!form.value.RequestId) errs.push('Request/Job is required.');
    if (!form.value.EstimatedTime) errs.push('Estimated Time is required.');
    if (!form.value.CompleteByDate) errs.push('Start By is required.');
    if (!form.value.WorkAssignment?.trim()) errs.push('Work Assignment is required.');

    errors.value = errs;
    return errs.length === 0;
};

defineExpose({ validate });
</script>
